import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Badge, Layout, Menu, SiderProps } from 'antd';
import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import { FEATURES, IS_PRODUCTION, TENANTS } from 'appConstants';
import { FeatureFlag } from 'components/FeatureFlag';
import MenuIcon from 'components/Header/MenuIcon';
import Ability from 'containers/Ability';
import Analytics from 'containers/Analytics';
import {
  getCampaignPendingRequest,
  getCustomerPendingRequest,
  getNecessities,
  getRewardPendingRequest,
  getRulePendingRequest
} from 'containers/App/actions';
import IdleHandler from 'containers/App/components/IdleHandler';
import UserMenu from 'containers/App/components/UserMenu';
import { Logo, Sider } from 'containers/App/style';
import Audience from 'containers/Audience';
import { useBDOLoyaltyEnabled } from 'containers/BDO/utils';
import { CI_SCREEN_OVERVIEW } from 'containers/Intelligence/permissions/constants';
import { getLoyaltyPrograms } from 'containers/Loyalty/actions';
import {
  ORBIT_PAGE_PATH,
  OrbitFullPage,
  OrbitNavLink,
  OrbitShell,
  useOrbitAccess
} from 'containers/Orbit';
import { getPermissions } from 'containers/Permissions/actions';
import { CI_ROOT } from 'containers/Setting/components/RoleFormModal/customerIntelligence';
import { OW_ROOT } from 'containers/Setting/components/RoleFormModal/overwatch';
import { useFeatureFlags, useTenant, useTypedSelector } from 'hooks';
import ForbiddenPage from 'pages/public/AccessDenied';
import { DashboardLoading, PageLoading } from 'pages/public/Loading';
import NotFoundPage from 'pages/public/NotFoundPage';
import { applyAiMasterFlag } from 'utils/aiFeatureFlags';
import { lazyWithRetry } from 'utils/lazyWithRetry';

import ReloadMessage from '../components/ReloadMessage';
import {
  AI_WORKSPACE_MENU_KEYS,
  INTELLIGENCE_SUITE_MENU_KEY,
  ORBIT_NAV_KEY,
  getFilteredMenu,
  getNavGroups,
  isIntelligenceSuiteGranted
} from '../getMenu';
import { usePendo } from '../usePendo';
import POSableLogo from './POSable_logo.png';
import AffirmLogo from './affirm_logo.png';
import { ReactComponent as PerxLogo } from './ic_logo.svg';
import PoweredByPerx from './powered_by_perx.png';
import RushLogo from './rush_logo.png';
import ZealLogo from './zeal_logo.png';
import './index.less';
import PointsRedemptions from 'containers/BDO/pointsRedemptions';
import CreditCardRedemptionSettings from 'containers/BDO/settings';

import ErrorFallback from '../components/ErrorFallback';
import { PerxState } from 'MyTypes';

const Settings = lazyWithRetry(() => import('containers/Setting'));
const LanguageSelector = lazyWithRetry(() => import('containers/LanguageSelector'));
const Leaderboards = lazyWithRetry(() => import('containers/Leaderboards'));
const Marketplace = lazyWithRetry(() => import('containers/Marketplace'));
const Report = lazyWithRetry(() => import('containers/Report'));
const Reward = lazyWithRetry(() => import('containers/Reward'));
const Catalogue = lazyWithRetry(() => import('containers/Catalogue'));
const Merchant = lazyWithRetry(() => import('containers/Merchant'));
const Campaign = lazyWithRetry(() => import('containers/Campaign'));
const Loyalty = lazyWithRetry(() => import('containers/Loyalty'));
const Customer = lazyWithRetry(() => import('containers/Customer'));
const BulkAction = lazyWithRetry(() => import('containers/BulkAction'));
const Rules = lazyWithRetry(() => import('containers/Rule'));
const Inventory = lazyWithRetry(() => import('containers/Inventory'));
const Partners = lazyWithRetry(() => import('containers/Partners'));
const Badges = lazyWithRetry(() => import('containers/Badge'));
const UserProfile = lazyWithRetry(() => import('containers/UserProfile'));
const Microsite = lazyWithRetry(() => import('containers/Microsite'));
const QuickSight = lazyWithRetry(() => import('containers/Analytics/QuickSight'));
const ScheduleTasks = lazyWithRetry(() => import('containers/ScheduleTasks'));
const CashbackRules = lazyWithRetry(() => import('containers/Cashback'));
const AgentControlPanel = lazyWithRetry(() => import('containers/AgentControlPanel'));
const Intelligence = lazyWithRetry(() => import('containers/Intelligence'));
const GrowthPlanner = lazyWithRetry(() => import('containers/GrowthPlanner'));
const ConfigStudio = lazyWithRetry(() => import('containers/ConfigStudio'));
const Overwatch = lazyWithRetry(() => import('containers/Overwatch'));

const Forbidden = () => <ForbiddenPage />;

const ProtectedReward = props => (
  <Ability renderNoAccess={Forbidden} access="rewards">
    <Reward {...props} />
  </Ability>
);

const ProtectedMarketplace = props => (
  <Ability renderNoAccess={Forbidden} access="rewards">
    <Marketplace {...props} />
  </Ability>
);

const ProtectedCatalog = props => (
  <Ability renderNoAccess={Forbidden} access="catalogs">
    <Catalogue {...props} />
  </Ability>
);

const ProtectedCampaign = props => (
  <Ability renderNoAccess={Forbidden} access="campaigns">
    <Campaign {...props} />
  </Ability>
);

const ProtectedMerchant = props => (
  <Ability renderNoAccess={Forbidden} access="merchants">
    <Merchant {...props} />
  </Ability>
);

const ProtectedLoyalty = props => (
  <Ability renderNoAccess={Forbidden} access="loyalties">
    <Loyalty {...props} />
  </Ability>
);

const ProtectedPartner = props => (
  <Ability renderNoAccess={Forbidden} access="partners">
    <Partners {...props} />
  </Ability>
);

const ProtectedSettings = props => (
  <Ability renderNoAccess={Forbidden} access="settings">
    <Settings {...props} />
  </Ability>
);

const ProtectedInventory = props => (
  <Ability renderNoAccess={Forbidden} access="inventories">
    <Inventory {...props} />
  </Ability>
);

const ProtectedSchedule = props => (
  <Ability renderNoAccess={Forbidden} access="scheduled_tasks">
    <ScheduleTasks {...props} />
  </Ability>
);

const ProtectedCashback = props => (
  <Ability renderNoAccess={Forbidden} access="cashback_rules">
    <CashbackRules {...props} />
  </Ability>
);

// Exported for `__tests__/intelligenceRoute.test.tsx`: mounting the whole protected route table
// would need the full app shell, so the gate composition is asserted directly. NOTE: as everywhere
// else in the app, `restricted_actions: ['index']` only hides the sidebar entry — it is a
// menu-presentation flag, not an access control, so a direct URL still reaches this gate. PLA-8815.
export const ProtectedIntelligence = () => (
  <FeatureFlag
    flags={[FEATURES.PERX_AI_WORKSPACE, FEATURES.INTELLIGENCE_SUITE]}
    altChildren={<Forbidden />}
  >
    {/* Both gates pass `isComponent`: without it `Ability` checks resource existence only, so a
        revoked root grant (`actions: []`) would still open the route while every card on it is
        omitted (`conventions/permissions/gotchas.md § 1, § 2`). */}
    <Ability isComponent renderNoAccess={Forbidden} access={CI_ROOT} actions={['view']}>
      <Ability
        isComponent
        renderNoAccess={Forbidden}
        access={CI_SCREEN_OVERVIEW}
        actions={['view']}
      >
        <Intelligence />
      </Ability>
    </Ability>
  </FeatureFlag>
);

// Exported for `__tests__/overwatchRoute.test.tsx`. PLA-9674.
export const ProtectedOverwatch = () => (
  <FeatureFlag
    flags={[FEATURES.PERX_AI_WORKSPACE, FEATURES.LAYER_6_DASHBOARD]}
    altChildren={<Forbidden />}
  >
    <Ability isComponent renderNoAccess={Forbidden} access={OW_ROOT} actions={['view']}>
      <Overwatch />
    </Ability>
  </FeatureFlag>
);

const RewardRequestsRedirect = () => (
  <Navigate to="/p/rewards" state={{ tab: 'pending' }} replace />
);

const RewardRequestCommentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const rewardCampaignId = new URLSearchParams(search).get('reward_campaign_id');

  return rewardCampaignId ? (
    <Navigate to={`/p/rewards/show/${rewardCampaignId}/reward_request/${id}`} replace />
  ) : (
    <Navigate to="/p/rewards" replace />
  );
};

const CampaignRequestsRedirect = () => (
  <Navigate to="/p/campaigns" state={{ tab: 'pending' }} replace />
);

const CampaignRequestCommentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const campaignId = new URLSearchParams(search).get('campaign_id');

  return campaignId ? (
    <Navigate to={`/p/campaigns/show/${campaignId}/campaign_request/${id}`} replace />
  ) : (
    <Navigate to="/p/campaigns" replace />
  );
};

const TransactionRulesRedirect = () => (
  <Navigate to="/p/rules" state={{ tab: 'pending' }} replace />
);

const RuleRequestCommentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const transactionRuleId = new URLSearchParams(search).get('transaction_rule_id');

  return transactionRuleId ? (
    <Navigate to={`/p/rules/show/${transactionRuleId}/rule_request/${id}`} replace />
  ) : (
    <Navigate to="/p/rules" replace />
  );
};

const BulkActionRedirect = () => <Navigate to="/p/bulk_actions" replace />;

const IndexPage = () => {
  usePendo();
  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useTypedSelector(state => state.permissions.permissions, shallowEqual);
  const { name: tenantName, features: tenantFeatures } = useTenant();
  const features = useMemo(() => applyAiMasterFlag(tenantFeatures), [tenantFeatures]);
  const [collapsedPreference, setCollapsedPreference] = useState(true);
  /*
    Orbit folds the sider while it has the screen by holding the DRAWN state shut, never by writing
    the preference, so handing the screen back restores whatever the preference is by then. The
    user's own toggle meanwhile releases the hold until Orbit next opens.
  */
  const [orbitActive, setOrbitActive] = useState(false);
  const [orbitHoldReleased, setOrbitHoldReleased] = useState(false);
  const collapsed = collapsedPreference || (orbitActive && !orbitHoldReleased);
  const onOrbitActiveChange = useCallback((active: boolean) => {
    setOrbitActive(active);
    setOrbitHoldReleased(false);
  }, []);
  const { currentTheme } = useThemeSwitcher();

  const makerCheckerCustomerManagement = features[FEATURES.MAKER_CHECKER_CUSTOMER_MANAGEMENT.key];
  const makerCheckerReward = features[FEATURES.MAKER_CHECKER_REWARD.key];
  const makerCheckerCampaign = features[FEATURES.MAKER_CHECKER_CAMPAIGN.key];
  const makerCheckerRule = features[FEATURES.MAKER_CHECKER_RULE.key];

  const customerPendingRequest = useSelector(
    (state: PerxState) => state.global.customerPendingRequest
  );
  const campaignPendingRequest = useSelector(
    (state: PerxState) => state.global.campaignPendingRequest
  );
  const rewardPendingRequest = useSelector((state: PerxState) => state.global.rewardPendingRequest);
  const rulePendingRequest = useSelector((state: PerxState) => state.global.rulePendingRequest);

  useEffect(() => {
    if (makerCheckerCustomerManagement && customerPendingRequest?.error?.status !== 404) {
      dispatch(getCustomerPendingRequest({ params: { status: 'pending' } }));
    }

    if (makerCheckerCampaign && campaignPendingRequest?.error?.status !== 404) {
      dispatch(getCampaignPendingRequest({ params: { status: 'pending' } }));
    }

    if (makerCheckerReward && rewardPendingRequest?.error?.status !== 404) {
      dispatch(getRewardPendingRequest({ params: { status: 'pending' } }));
    }

    if (makerCheckerRule && rulePendingRequest?.error?.status !== 404) {
      dispatch(getRulePendingRequest({ params: { status: 'pending' } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    makerCheckerCustomerManagement,
    makerCheckerCampaign,
    makerCheckerReward,
    makerCheckerRule
  ]);

  const {
    audience: showAudience,
    catalogs: showCatalogs,
    inventories: showInventory,
    reward_budgeting: showPartners,
    rewards_marketplace: showMarketplace,
    leaderboards: showLeaderboards,
    badges: showBadges,
    loyalties: showLoyalties,
    microsite: showMicrosite,
    merchants: showMerchants,
    quicksight: showQuickSight,
    scheduled_tasks: showScheduleTasks,
    language_selector: languageSelector,
    cashback: showCashbackRules,
    agent_control_panel: showAgentControlPanel
  } = useFeatureFlags();

  const [selectedMenu, setSelectedMenu] = useState(
    _isEmpty(permissions) ? '' : Object.keys(permissions)[0]
  );

  // antd passes the state to draw, not a toggle: with Orbit holding the sider shut, flipping the
  // preference would turn a click on "expand" into a collapse.
  const onSidebarCollapse: SiderProps['onCollapse'] = (value, type) => {
    if (type === 'clickTrigger') {
      setOrbitHoldReleased(true);
    }
    setCollapsedPreference(value);
  };

  /*
    Groups are open on arrival and stay open until the user shuts one. Controlled rather than left
    to an initial-value prop, because the menu mounts before permissions resolve: at mount there
    are no submenus for an initial value to apply to, so the groups all render shut once they
    appear.
  */
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [userTouchedGroups, setUserTouchedGroups] = useState(false);

  const onMenuClick = evt => setSelectedMenu(evt.key);

  useEffect(() => {
    // get audience list
    // get tags
    // get labaels
    // get categories
    dispatch(getNecessities());
    dispatch(getLoyaltyPrograms());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPermissions());
    // eslint-disable-next-line
  }, [tenantName]);

  const bdoLoyaltyEnabled = useBDOLoyaltyEnabled();
  const orbitAllowed = useOrbitAccess() === 'allowed';
  /*
    The AI WorkSpace umbrella is the master flag, not Intelligence Suite - that is one module under
    the umbrella, not the umbrella itself. Growth Planner, Config Studio and Perx OverWatch have no
    backend permission of their own, so without this gate they were visible to every tenant. Figma
    variant 123:132 draws no group at all. PLA-8844.
  */
  const aiWorkspaceEnabled = features[FEATURES.PERX_AI_WORKSPACE.key] === true;
  const intelligenceSuiteEnabled = isIntelligenceSuiteGranted(permissions, features);

  const menu = useMemo(() => {
    // every time permission changes, get the filtered menu items from MENU
    const modifiedPermissions = {
      ...permissions,
      quicksight: true,
      marketplace: showMarketplace,
      cashback_rules: true,
      language_selector: languageSelector,
      agent_control_panel: showAgentControlPanel
    };

    /*
      getFilteredMenu gates on KEY PRESENCE, not on the value - a real permission is an object, so
      Object.keys() sees these whatever they are set to. Assigning them `aiWorkspaceEnabled` left
      all four rows visible to a tenant without the flag, appended after the last group because no
      nav group claims them. The keys have to be absent, not false. PLA-8844.
    */
    if (aiWorkspaceEnabled) {
      AI_WORKSPACE_MENU_KEYS.forEach(key => {
        modifiedPermissions[key] = true;
      });
    } else {
      AI_WORKSPACE_MENU_KEYS.forEach(key => delete modifiedPermissions[key]);
    }

    if (aiWorkspaceEnabled && intelligenceSuiteEnabled) {
      modifiedPermissions[INTELLIGENCE_SUITE_MENU_KEY] = true;
    } else {
      delete modifiedPermissions[INTELLIGENCE_SUITE_MENU_KEY];
    }

    let newMenu = getFilteredMenu(modifiedPermissions, features);

    if (!showAudience) {
      newMenu = newMenu.filter(x => x.key !== 'audience');
    }

    if (!showScheduleTasks) {
      newMenu = newMenu.filter(x => x.key !== 'scheduled_tasks');
    }

    if (!showCatalogs) {
      newMenu = newMenu.filter(x => x.key !== 'catalogs');
    }

    if (!showInventory) {
      newMenu = newMenu.filter(x => x.key !== 'inventories');
    }

    if (!showMarketplace) {
      newMenu = newMenu.filter(x => x.key !== 'marketplace');
    }

    if (!showPartners) {
      newMenu = newMenu.filter(x => x.key !== 'partners');
    }

    if (!showLeaderboards) {
      newMenu = newMenu.filter(x => x.key !== 'leaderboards');
    }

    if (!showBadges) {
      newMenu = newMenu.filter(x => x.key !== 'badges');
    }

    if (!showLoyalties) {
      newMenu = newMenu.filter(x => x.key !== 'loyalties');
    }

    if (!showMicrosite) {
      newMenu = newMenu.filter(x => x.key !== 'microsites');
    }

    if (!showMerchants) {
      newMenu = newMenu.filter(x => x.key !== 'merchants');
    }

    if (!bdoLoyaltyEnabled) {
      newMenu = newMenu.filter(x => x.key !== 'points_redemptions');
    }

    if (!showQuickSight) {
      newMenu = newMenu.filter(x => x.key !== 'quicksight');
    }

    if (!languageSelector) {
      newMenu = newMenu.filter(x => x.key !== 'language_selector');
    }

    if (!showCashbackRules) {
      newMenu = newMenu.filter(x => x.key !== 'cashback_rules');
    }

    if (!showAgentControlPanel) {
      newMenu = newMenu.filter(x => x.key !== 'agent_control_panel');
    }

    return newMenu;
    // Since features is part of the dependency, so we don't need individuals
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bdoLoyaltyEnabled, permissions, tenantName, features]);

  /**
   * Sidebar rows. Expanded, they are grouped per Figma 7:1447. Collapsed, they are the same rows
   * flattened: antd draws a collapsed inline menu as icon-only, and a group header has no icon, so
   * grouping a collapsed sider would replace the glyphs with blank rows. The design has no collapsed
   * frame (design/config-studio-nav-spec.md 3) and collapsed is the state the dashboard opens in.
   *
   * Perx Orbit is synthesised here rather than living in getFullMenu: it has no route, so a real
   * entry would make it a landing destination in saga.ts. PLA-8844.
   */
  const menuItems = useMemo(() => {
    const orbitItem = { key: ORBIT_NAV_KEY, description: 'Perx Orbit', svg: 'perx_orbit' };
    const byKey = new Map(menu.map(item => [item.key, item]));

    const resolve = key => {
      if (key === ORBIT_NAV_KEY) {
        return orbitAllowed ? orbitItem : undefined;
      }
      return byKey.get(key);
    };

    const renderItem = item => ({
      key: item.key,
      label:
        item.key === ORBIT_NAV_KEY ? (
          <OrbitNavLink>{item.description}</OrbitNavLink>
        ) : (
          <NavLink to={item.url}>
            {item.description}
            {makerCheckerRule &&
            item.key === 'transaction_rules' &&
            rulePendingRequest?.meta?.count > 0 ? (
              <Badge status="processing" style={{ marginLeft: '5px' }} />
            ) : null}

            {makerCheckerReward &&
            item.key === 'rewards' &&
            rewardPendingRequest?.meta?.count > 0 ? (
              <Badge status="processing" style={{ marginLeft: '5px' }} />
            ) : null}

            {makerCheckerCustomerManagement &&
            item.key === 'customer_management' &&
            customerPendingRequest?.meta?.count > 0 ? (
              <Badge status="processing" style={{ marginLeft: '5px' }} />
            ) : null}

            {makerCheckerCampaign &&
              item.key === 'campaigns' &&
              campaignPendingRequest?.meta?.count > 0 && (
                <Badge status="processing" style={{ marginLeft: '5px' }} />
              )}
          </NavLink>
        ),
      /*
        Many glyphs carry fill={color} on their PATHS, which a stylesheet rule on the root svg
        cannot reach, so the colour has to come through the prop. Without this they render
        MenuIcon's #9b9b9b default instead of the design's #BFBFBF, and never repaint on select.
      */
      icon: (
        <MenuIcon
          src={item.svg}
          className="menu-icon"
          color={item.key === selectedMenu ? '#ffffff' : '#bfbfbf'}
        />
      )
    });

    /* The grouped, collapsible sidebar ships with the AI WorkSpace. Without the flag a tenant keeps
       the flat list it has always had - same rows, same order, no group headers. PLA-8844. */
    if (!aiWorkspaceEnabled) {
      return menu.map(renderItem);
    }

    // A key no group claims still gets a row, so adding to getFullMenu without touching the nav groups
    // cannot silently hide it.
    const navGroups = getNavGroups(aiWorkspaceEnabled, intelligenceSuiteEnabled);
    /* A key on its own, or a parent with rows indented under it. */
    const keysOf = entry => (typeof entry === 'string' ? [entry] : [entry.key, ...entry.children]);
    const flatKeys = group => group.keys.reduce((all, entry) => all.concat(keysOf(entry)), []);
    const groupedKeys = new Set(
      navGroups.reduce((keys, group) => keys.concat(flatKeys(group)), [])
    );
    const ungrouped = menu.filter(item => !groupedKeys.has(item.key));

    /* Collapsed is icon-only, where nesting cannot read, so the rows flatten in the same order. */
    if (collapsed) {
      const flat = navGroups.reduce(
        (rows, group) => rows.concat(flatKeys(group).map(resolve).filter(Boolean)),
        []
      );
      return flat.concat(ungrouped).map(renderItem);
    }

    const renderEntry = entry => {
      if (typeof entry === 'string') {
        const item = resolve(entry);
        return item ? renderItem(item) : null;
      }

      const parent = resolve(entry.key);
      /* Indented siblings, not a submenu: the parent reads as a label and navigates, and its rows
         stay visible rather than hiding behind a toggle. */
      const children = entry.children
        .map(resolve)
        .filter(Boolean)
        .map(item => ({ ...renderItem(item), className: 'nav-child' }));

      /* Parent gated away: its children stand on their own rather than disappearing with it. */
      if (!parent) {
        return children.length ? children : null;
      }

      return [renderItem(parent), ...children];
    };

    const groups = navGroups
      .map(group => {
        const children = group.keys
          .map(renderEntry)
          .filter(Boolean)
          .reduce((all, node) => all.concat(node), []);

        return children.length
          ? { key: `group:${group.label}`, label: group.label, children }
          : null;
      })
      .filter(Boolean);

    return [...groups, ...ungrouped.map(renderItem)];
  }, [
    collapsed,
    menu,
    selectedMenu,
    orbitAllowed,
    aiWorkspaceEnabled,
    intelligenceSuiteEnabled,
    makerCheckerRule,
    rulePendingRequest,
    makerCheckerReward,
    rewardPendingRequest,
    makerCheckerCustomerManagement,
    customerPendingRequest,
    makerCheckerCampaign,
    campaignPendingRequest
  ]);

  const groupKeys = useMemo(
    () =>
      aiWorkspaceEnabled
        ? getNavGroups(aiWorkspaceEnabled, intelligenceSuiteEnabled).map(
            group => `group:${group.label}`
          )
        : [],
    [aiWorkspaceEnabled, intelligenceSuiteEnabled]
  );

  useEffect(() => {
    if (!userTouchedGroups) {
      setOpenGroups(groupKeys);
    }
  }, [groupKeys, userTouchedGroups]);

  useEffect(() => {
    if (location.pathname.startsWith(ORBIT_PAGE_PATH)) {
      setSelectedMenu(ORBIT_NAV_KEY);
      return;
    }
    const activeMenuItem = menu.find(element => location.pathname.includes(element.url));
    if (activeMenuItem && activeMenuItem.key) {
      setSelectedMenu(_ => activeMenuItem.key);
    }
  }, [location.pathname, menu]);

  const showPoweredByPerx = tenantName === TENANTS.CWC;

  if (!tenantName) {
    return <DashboardLoading />;
  }

  const appSider = (
    <ScrollableSider
      breakpoint="lg"
      width={aiWorkspaceEnabled ? 230 : 200}
      collapsible
      collapsed={collapsed}
      onCollapse={onSidebarCollapse}
      /*
        antd only reports a breakpoint crossing that changes the drawn state, and none does while
        Orbit holds the sider shut, so without this a narrow screen would expand it on hand-back.
      */
      onBreakpoint={setCollapsedPreference}
      style={{
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: 20, // need this to put sider on top of forms\' bottom bar, TODO: reevaluate after looking into BottomBar
        display: 'flex',
        flexDirection: 'column'
      }}
      className={clsx(currentTheme, aiWorkspaceEnabled && 'nav-v2')}
    >
      <Logo>
        <Link to={'/'}>
          <AppLogo />
        </Link>
      </Logo>
      <UserMenu />
      <Menu
        theme="dark"
        mode="inline"
        style={{ borderRight: 'none' }}
        onClick={onMenuClick}
        selectedKeys={[selectedMenu]}
        openKeys={collapsed ? undefined : openGroups}
        onOpenChange={keys => {
          // Collapsing makes rc-menu report every group shut; the groups to reopen are kept.
          if (collapsed) {
            return;
          }
          setUserTouchedGroups(true);
          setOpenGroups(keys as string[]);
        }}
        items={menuItems}
      />
      {showPoweredByPerx && <PoweredBy alt="Powered by Perx" src={PoweredByPerx} />}
    </ScrollableSider>
  );

  return (
    <Layout>
      <IdleHandler />
      <OrbitShell sider={appSider} onActiveChange={onOrbitActiveChange}>
        <Layout
          style={{
            //overflow should be unset to allow position sticky items to work in content
            overflowX: 'unset',
            //min-width is set to auto for flex items cause it to expand based on content. This performs same function as overflow-x hidden
            minWidth: 0
          }}
        >
          <Layout.Content>
            <Sentry.ErrorBoundary
              fallback={({ error, eventId }) =>
                error.name === 'ChunkLoadError' ? (
                  <ReloadMessage />
                ) : (
                  <ErrorFallback eventId={eventId} />
                )
              }
            >
              <Suspense fallback={<PageLoading />}>
                <Routes>
                  <Route
                    index
                    element={
                      !_isEmpty(permissions) && menu[0] ? (
                        <Navigate to={menu[0].url} replace />
                      ) : (
                        <PageLoading />
                      )
                    }
                  />
                  <Route path="reports/*" element={<Report />} />
                  {showMarketplace && (
                    <Route path="marketplace/*" element={<ProtectedMarketplace />} />
                  )}
                  <Route path="rewards/*" element={<ProtectedReward />} />
                  <Route path="reward_requests" element={<RewardRequestsRedirect />} />
                  <Route
                    path="reward_requests/:id/comments"
                    element={<RewardRequestCommentsRedirect />}
                  />
                  <Route path="catalogues/*" element={<ProtectedCatalog />} />
                  {showMerchants && <Route path="merchants/*" element={<ProtectedMerchant />} />}
                  <Route path="campaigns/*" element={<ProtectedCampaign />} />
                  <Route path="campaign_requests" element={<CampaignRequestsRedirect />} />
                  <Route
                    path="campaign_requests/:id/comments"
                    element={<CampaignRequestCommentsRedirect />}
                  />
                  {showBadges && <Route path="badges/*" element={<Badges />} />}
                  <Route
                    path="leaderboards/*"
                    element={
                      <FeatureFlag flags={[FEATURES.LEADERBOARDS]}>
                        <Leaderboards />
                      </FeatureFlag>
                    }
                  />
                  {showLoyalties && <Route path="loyalty/*" element={<ProtectedLoyalty />} />}
                  <Route path="settings/*" element={<ProtectedSettings />} />
                  <Route path="scheduled-tasks/*" element={<ProtectedSchedule />} />
                  {languageSelector && (
                    <Route path="language_selector/*" element={<LanguageSelector />} />
                  )}
                  <Route path="customers/*" element={<Customer />} />
                  <Route path="bulkaction" element={<BulkActionRedirect />} />
                  <Route path="bulk_actions/*" element={<BulkAction />} />
                  <Route path="business_intelligence/*" element={<Analytics />} />
                  {showAgentControlPanel && (
                    <Route path="agent-control-panel/*" element={<AgentControlPanel />} />
                  )}
                  {showQuickSight && <Route path="quicksight/*" element={<QuickSight />} />}
                  <Route path="intelligence/*" element={<ProtectedIntelligence />} />
                  <Route
                    path="growth-planner/*"
                    element={
                      <FeatureFlag flags={[FEATURES.PERX_AI_WORKSPACE]} altChildren={<Forbidden />}>
                        <GrowthPlanner />
                      </FeatureFlag>
                    }
                  />
                  <Route
                    path="config-studio/*"
                    element={
                      <FeatureFlag flags={[FEATURES.PERX_AI_WORKSPACE]} altChildren={<Forbidden />}>
                        <ConfigStudio />
                      </FeatureFlag>
                    }
                  />
                  <Route path="overwatch/*" element={<ProtectedOverwatch />} />
                  <Route path="orbit/*" element={<OrbitFullPage />} />
                  <Route path="rules/*" element={<Rules />} />
                  <Route path="transaction_rules" element={<TransactionRulesRedirect />} />
                  {showCashbackRules && (
                    <Route path="cashback_rules/*" element={<ProtectedCashback />} />
                  )}
                  <Route
                    path="rule_requests/:id/comments"
                    element={<RuleRequestCommentsRedirect />}
                  />
                  <Route path="inventories/*" element={<ProtectedInventory />} />
                  {showPartners && <Route path="partners/*" element={<ProtectedPartner />} />}
                  {showMicrosite && <Route path="microsite/*" element={<Microsite />} />}
                  <Route path="audiences/*" element={<Audience />} />
                  <Route path="user-profile/*" element={<UserProfile />} />
                  <Route path="points_redemptions/*" element={<PointsRedemptions />} />
                  <Route
                    path="credit_card_redemption_settings/*"
                    element={<CreditCardRedemptionSettings />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </Sentry.ErrorBoundary>
          </Layout.Content>
        </Layout>
      </OrbitShell>
    </Layout>
  );
};

const ScrollableSider = styled(Sider)`
  /*
    Sidebar geometry from Figma 7:1447 (Perx Config Studio). antd writes inline padding on inline
    menu rows and computes its own heights, so the row rules need &&& to land. PLA-8844.
  */
  &&&.nav-v2 {
    background: #212d47;

    .ant-menu,
    .ant-menu-sub {
      background: transparent;
    }

    .ant-menu-root.ant-menu-inline {
      padding-top: 8px;
    }

    /* Group body: no padding of its own — the rows supply all the spacing. */
    .ant-menu-sub.ant-menu-inline {
      padding: 0;
    }

    /* antd writes an inline, depth-derived padding-left on every inline row, which outranks a
       stylesheet rule however specific, so the two row indents below need !important. */

    /* Group header row: 44 high, 24px gutters, no fill. Figma draws it 250 wide; the sider
       is 230 by request, and the row is edge-anchored so only the width differs. */
    .ant-menu-submenu-title {
      height: 44px;
      line-height: 44px;
      margin: 0;
      padding-left: 24px !important;
      padding-right: 24px !important;
      color: #bfbfbf;
      font-weight: 500;
      font-size: 14px;
      letter-spacing: -0.1504px;
    }

    .ant-menu-submenu-arrow {
      right: 24px;
    }

    /* An open group body carries its own darker fill. */
    .ant-menu-submenu-open > .ant-menu-sub {
      background: #1a2538;
    }

    /* Nav row: 44 high, icon box at x28, label at x64 (28 + 20 + 16). Figma indents the row 40
       against a 250 sider; at 230 that reads heavy, so it is 28 — still clear of the 24px group
       header, so rows keep reading as nested. */
    /* Top-level rows indent 28 and the rows nested under Intelligence suite indent 56. antd writes
       its own depth-derived padding inline, so both need !important. */
    .ant-menu-item {
      height: 44px;
      line-height: 44px;
      margin: 0;
      padding-left: 28px !important;
      padding-right: 24px !important;
      border-radius: 0;
      color: #bfbfbf;
      font-size: 14px;
      letter-spacing: -0.1504px;

      /* antd offsets the label span 10px from the icon on top of our own gap, which the x64
         arithmetic above does not allow for; it is what truncated "Customer Insights". */
      .ant-menu-item-icon + span,
      .anticon + span {
        margin-left: 0;
      }

      .menu-icon {
        width: 20px;
        min-width: 20px;
        margin-right: 16px;

        > svg {
          fill: #bfbfbf;
        }
      }

      a {
        color: #bfbfbf;
      }
    }

    /* Rows indented under a parent row. Figma 268:243 draws their icon gap at 8, not the 16 a
       top-level row gets. */
    .ant-menu-item.nav-child {
      padding-left: 56px !important;

      .menu-icon {
        margin-right: 8px;
      }
    }

    /* Active row: white label + icon, no row background - only the 5px bar from Sider. */
    .ant-menu-item-selected {
      color: #ffffff;

      a {
        color: #ffffff;
      }

      .menu-icon > svg {
        fill: #ffffff;
      }
    }
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;

    .ant-menu {
      flex: 1;
      overflow-y: auto;
      scrollbar-width: none;

      ::-webkit-scrollbar {
        display: none;
      }
    }
  }
`;
const PoweredBy = styled.img`
  margin: 0 auto;
  width: 64px;
`;

export default IndexPage;

const AppLogo = () => {
  const { name: tenantName } = useTenant();
  let logo;

  switch (tenantName) {
    case TENANTS.ZEAL:
      logo = IS_PRODUCTION ? (
        <LogoImage src={ZealLogo} alt="Logo" width={'50'} />
      ) : (
        <LogoImage src={POSableLogo} alt="Logo" width={'50'} />
      );
      break;
    case TENANTS.RUSH:
    case TENANTS.CWC:
      logo = <LogoImage src={RushLogo} alt="Logo" style={{ width: 120 }} />;
      break;
    case TENANTS.AFFIRM:
      logo = <LogoImage src={AffirmLogo} alt={'Logo'} width={'85'} />;
      break;
    default:
      logo = <PerxLogo width={'50px'} />;
      break;
  }

  return logo;
};

const LogoImage = styled.img`
  max-width: 100%;
`;
